import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { CountryInfo } from '@sharedModule/models/country.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { GlobalService } from '@sharedModule/services/global.service';
import {
  loadDestinationCountry,
  deleteDestCountry,
} from '@sharedModule/store/destination-country/destination-country.actions';
import {
  selectDestSourceCurrency,
  selectDestinationCountry,
} from '@sharedModule/store/destination-country/destination-country.selector';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import { Subject, takeUntil } from 'rxjs';
import { CountrySettingsService } from '../../services/country-settings.service';

@Component({
  selector: 'app-destination-country',
  standalone: true,
  imports: [
    CommonModule,
    InputModule,
    ReactiveFormsModule,
    MaterialModule,
    HasAccessDirective,
  ],
  templateUrl: './destination-country.component.html',
  styleUrl: './destination-country.component.scss',
})
export class DestinationCountryComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.countrySettings;
  displayedColumns: string[] = [
    'countryName',
    'countryCode',
    'currencySupported',
    'action',
  ];
  filterForm!: FormGroup;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataSource!: MatTableDataSource<any>;
  sourceCountryList!: CountryInfo[];
  selectedSourceCountry!: CountryInfo;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private globalService: GlobalService,
    private messageHandlerService: MessageHandlerService,
    private countrySettingsService: CountrySettingsService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.sourceCountry.key]: new FormControl(''),
    });
    this.store
      .select(selectSourceCountry)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.sourceCountryList = data;
          if (this.sourceCountryList.length !== 0) {
            this.filterForm.patchValue({
              [this.attrConstant.sourceCountry.key]:
                this.sourceCountryList[0].countryCode,
            });
            this.onCountrySelect({
              value: this.sourceCountryList[0].countryCode,
              selectOptions: this.sourceCountryList,
            });
          }
        }
      });
    this.store
      .select(selectDestSourceCurrency)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) this.selectedSourceCountry = data;
      });
  }

  onEdit(data: CountryInfo): void {
    this.globalService.putDataToStream({
      data,
      sourceCountry: this.selectedSourceCountry,
    });
    this.router.navigate(['/settings/edit-destination-country']);
  }

  onCountrySelect(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    this.store.dispatch(
      loadDestinationCountry({ payload: { src_country: event.value } })
    );
    this.store
      .select(selectDestinationCountry)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) this.dataSource = new MatTableDataSource(data);
      });
  }

  async onDeleteDestCountry(data: CountryInfo): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this destination country?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.countrySettingsService
        .deleteDestCountry({
          srcCountryName: this.selectedSourceCountry.countryName,
          destCountryName: data.countryName,
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(
                deleteDestCountry({
                  srcCountryName: this.selectedSourceCountry.countryName,
                  destCountryName: data.countryName,
                })
              );
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
